import {
  PLAY_TRACK,
  PLAY_SOUND_EFFECT,
  SEEK_TRACK,
  SET_PLAYLIST,
  SWITCH_LOOP_MODE,
  SWITCH_RANDOM_MODE,
  TRACK_PAUSED,
  TRACK_PLAYING,
} from '@App/store/actions/player';

const PLAY_STATE = {
  LOADING: 'LOADING',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
};

const initialState = {
  trackReference: null,
  versionReference: null,
  startPosition: null,
  /**
     * When set, the active item is a Pro Sound Effects sound effect played directly from its
     * signed preview URL (no `GetTrack` round-trip). `null` for regular music tracks.
     *
     * @type {?{id: String, previewUrl: String, title: String, duration: Number, waveformDataUrl: ?String}}
     */
  soundEffect: null,
  playlist: null,
  modeLoop: false,
  modeRandom: false,
  randomPlaylist: null,
  /**
     * If the active track is actually loading, playing or paused
     *
     * @type {PLAY_STATE}
     **/
  playState: PLAY_STATE.LOADING,
  history: [] as string[],
};

/**
 * Is player active?
 *
 * @param  {Object}  state Redux store
 *
 * @return {Boolean}
 */
export function isPlayerActive(state) {
  return state.player.trackReference !== null;
}

/**
 * Get next track in playlist
 *
 * @param  {Object} state Redux store
 *
 * @return {Object} Object with keys: trackReference, versionReference, highlights.
 */
export function getNextTrack(state) {
  let nextTrack = getTrack(state, 1);

  // Loop back to the first entry only when the current item actually belongs to the active
  // playlist (getTrack(state, 0) locates it). Otherwise — e.g. a sound-effect preview while a
  // music playlist is still loaded, or vice versa — looping would return an unrelated entry of
  // the other media type and the player would jump domains.
  if (null === nextTrack && state.player.modeLoop && getTrack(state, 0) !== null) {
    nextTrack = getFirstTrack(state);
  }

  return nextTrack;
}

/**
 * Get previous track in playlist
 *
 * @param  {Object} state Redux store
 *
 * @return {Object} Object with keys: trackReference, versionReference, highlights.
 */
export function getPreviousTrack(state) {
  return getTrack(state, -1);
}

export function isPlayingTrack(state, trackReference) {
  return state.player.trackReference === trackReference && state.player.playState === PLAY_STATE.PLAYING;
}

export function isPausedTrack(state, trackReference) {
  return state.player.trackReference === trackReference && state.player.playState === PLAY_STATE.PAUSED;
}

export function isLoadingTrack(state, trackReference) {
  return state.player.trackReference === trackReference && state.player.playState === PLAY_STATE.LOADING;
}

export function isCurrentTrack(state, trackReference) {
  return state.player.trackReference === trackReference;
}

function getTrack(state, step = 1) {
  const { playlist, randomPlaylist, modeRandom, trackReference, versionReference } = state.player;

  const currentPlaylist = modeRandom ? randomPlaylist : playlist;
  if (currentPlaylist === null) {
    return null;
  }

  const index = currentPlaylist.findIndex(element => element.trackReference === trackReference && element.versionReference === versionReference);

  if (index < 0) {
    return null;
  }

  if (typeof currentPlaylist[index + step] === 'undefined') {
    return null;
  }

  return currentPlaylist[index + step];
}

function getFirstTrack(state) {
  const { playlist, randomPlaylist, modeRandom } = state.player;

  const currentPlaylist = modeRandom ? randomPlaylist : playlist;
  if (currentPlaylist === null) {
    return null;
  }

  return currentPlaylist[0];
}

function shuffle(statePlaylist, trackReference, versionReference) {
  const playlist = [...statePlaylist];
  let currentIndex = playlist.length,  randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex !== 0) {

    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [playlist[currentIndex], playlist[randomIndex]] = [playlist[randomIndex], playlist[currentIndex]];
  }
  // Move current track to the top of the playlist
  const index = playlist.findIndex(element => element.trackReference === trackReference
        && element.versionReference === versionReference);

  // Move the current entry to the top, preserving its full payload (soundEffect, highlights, …)
  // rather than rebuilding a bare { trackReference, versionReference } — a rebuilt sound-effect
  // entry would drop its `soundEffect` field and break auto-advance/looping on /effets-sonores.
  if (index >= 0) {
    const [current] = playlist.splice(index, 1);
    playlist.unshift(current);
  }

  return playlist;
}

/**
 * Audio player
 *
 * @param {Object} state
 * @param {Object} action
 *
 * @return {Object}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function player(state = initialState, action): any {
  const { type, payload } = action;
  const MAX_HISTORY_SIZE = 50;

  switch (type) {
    case PLAY_TRACK:
      return {
        ...state,
        trackReference: payload.trackReference,
        versionReference: payload.versionReference,
        startPosition: payload.startPosition,
        soundEffect: null,
        playState: PLAY_STATE.LOADING,
        highlights: payload.highlights,
        history: state.history.slice(-(MAX_HISTORY_SIZE - 1)).concat([ payload.versionReference ]),
      };

    case PLAY_SOUND_EFFECT:
      return {
        ...state,
        // Reuse trackReference as the player's identity slot so the existing
        // isPlayingTrack/isLoadingTrack selectors and playlist lookup keep working.
        trackReference: payload.soundEffect.id,
        versionReference: null,
        startPosition: null,
        soundEffect: payload.soundEffect,
        playState: PLAY_STATE.LOADING,
        highlights: [],
        history: state.history.slice(-(MAX_HISTORY_SIZE - 1)).concat([ payload.soundEffect.id ]),
      };

    case SEEK_TRACK:
      return {
        ...state,
        startPosition: payload.offset,
      };

    case SWITCH_LOOP_MODE: {
      const modeLoop = !state.modeLoop;
      return {
        ...state,
        modeLoop: modeLoop,
      };
    }

    case SWITCH_RANDOM_MODE: {
      const modeRandom =  !state.modeRandom;
      const randomPlaylist = modeRandom ? shuffle(
        state.playlist,
        state.trackReference,
        state.versionReference,
      ) : null;

      return {
        ...state,
        modeRandom: modeRandom,
        randomPlaylist: randomPlaylist,
      };
    }

    case TRACK_PLAYING:
      return { ...state, playState: PLAY_STATE.PLAYING };

    case TRACK_PAUSED:
      return { ...state, playState: PLAY_STATE.PAUSED };

    case SET_PLAYLIST:
      return { ...state, playlist: payload.playlist };

    default:
      return state;
  }
}
